import { memo, useCallback } from "react";
import { Button } from "components/ui/button";
import { cn } from "@/lib/utils";
import { ListBulletsIcon, KanbanIcon } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { FilterBar } from "shared/components/filterBar";
import { HelpdeskKanbanView } from "../components/HelpdeskKanbanView";
import { ExcelExportModal } from "shared/components/ExcelExportModal";
import { HELPDESK_EXPORT_COLUMNS } from "../configs/helpdeskExportColumns";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { getStatusLabel } from "../helpdeskPhaseConfig";
import { QuickAddTicketModal } from "../components/quickAddTicketModal";
import { CreateInsuranceIssueModal } from "../components/createInsuranceIssueModal";
import { MergeTicketsModal } from "../components/mergeTicketsModal";
import { DeleteTicketModal } from "../components/deleteTicketModal";
import { SupplierSuggestionModal } from "../components/supplierSuggestionModal";
import { useHelpdeskShared } from "../shared/hooks/useHelpdeskShared";
import { useNavigateWithFilters } from "../shared";
import { TicketInternalStatus } from "shared/types/helpdesk";

/**
 * HelpdeskKanbanPage - Kanban view for Helpdesk tickets
 *
 * This component renders the helpdesk tickets in a Kanban board format.
 * It uses the useHelpdeskShared hook to share state with HelpdeskListPage.
 *
 * Features:
 * - HelpdeskKanbanView with per-column pagination
 * - FilterBar with smart insights
 * - Column visibility controls
 * - Font size controls
 * - View mode toggle (List/Kanban)
 * - Statistics badges (Open/Completion/Total counts)
 * - All modals (QuickAdd, Merge, Delete, Insurance, Excel, Bulk Phase, Column Phase, Supplier Suggestion)
 */
const HelpdeskKanbanPageComponent = () => {
  const { session } = useSession();
  const navigate = useNavigateWithFilters();

  // Use shared hook with 'kanban' view mode
  const state = useHelpdeskShared("kanban");

  const {
    // Data
    slicedKanbanTickets,
    displayedNonClosedTickets,
    totalCount,
    openCount,
    completionCount,
    // isLoadingTotalCount, // unused
    // hasFilters, // unused
    columnPagination,

    // Filter states
    searchText,
    setSearchText,
    filterBarSections,
    filterBarChips,
    isRestoringFromURL,
    handleFiltersFromURL,

    // Actions
    refreshTickets,
    handleUpdateTicket,

    // Bulk actions
    handleExcelExportWithColumns,

    // Kanban-specific states
    hiddenKanbanColumns,
    kanbanFontSize,
    handleIncreaseFontSize,
    handleDecreaseFontSize,
    handleToggleColumnVisibility,
    setVisibleCountPerColumn,
    columnCountsMap,
    // Don't use handleColumnHeaderClick from shared hook - we'll override it below

    // Modal states
    quickAddModalOpen,
    setQuickAddModalOpen,
    mergeModalOpen,
    setMergeModalOpen,
    mergeSource,
    setMergeSource,
    mergeTarget,
    setMergeTarget,
    mergeSearchSource,
    setMergeSearchSource,
    mergeSearchTarget,
    setMergeSearchTarget,
    deleteModal,
    setDeleteModal,
    insuranceModalOpen,
    setInsuranceModalOpen,
    selectedTicketForInsurance,
    supplierSuggestionOpen,
    setSupplierSuggestionOpen,
    suggestedSuppliers,
    suggestionForLabel,
    suggestionForTicketIds,
    isUpdatingSupplier,
    applySuggestedSupplier,
    excelExportModalOpen,
    setExcelExportModalOpen,
    bulkPhasePickerOpen,
    setBulkPhasePickerOpen,
    handleBulkPhaseConfirm,
    columnPhasePickerOpen,
    setColumnPhasePickerOpen,
    selectedColumnPhase,
    handleColumnPhaseConfirm,
  } = state;

  // Override column header click to navigate to list view with phase filter
  const handleColumnHeaderClick = useCallback(
    (status: TicketInternalStatus) => {
      // Get current URL params
      const currentParams = new URLSearchParams(window.location.search);

      // Get current status filters (if any)
      const existingStatusFilters = currentParams.get("status");
      const statusArray = existingStatusFilters
        ? existingStatusFilters.split(",")
        : [];

      // Get the status label for the clicked column
      const statusLabel = getStatusLabel(status);

      // Add the clicked status to filters if not already present
      if (!statusArray.includes(statusLabel)) {
        statusArray.push(statusLabel);
      }

      // Navigate to list view with updated status filter
      // useNavigateWithFilters automatically preserves all other filters
      navigate("/helpdesk/list", {
        additionalParams: { status: statusArray.join(",") },
      });
    },
    [navigate],
  );

  // Handle fetch all tickets in a specific phase
  const handleFetchAllInColumn = useCallback(
    (status: TicketInternalStatus) => {
      // Get the status label for display
      const statusLabel = getStatusLabel(status);

      // Set the visible count for this column to 9999 to show all loaded tickets
      setVisibleCountPerColumn((prev) => ({
        ...prev,
        [status]: 9999,
      }));

      // Count how many tickets we're now showing
      const totalInColumn = columnCountsMap[status] || 0;

      toast.success(
        `Alle ${totalInColumn} tickets in fase "${statusLabel}" worden nu getoond`,
      );
    },
    [setVisibleCountPerColumn, columnCountsMap],
  );

  // Handle merge confirmation
  const handleMergeConfirm = async () => {
    if (!mergeSource || !mergeTarget) {
      toast.error("Selecteer zowel een bron als doel ticket");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets/merge`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceId: mergeSource.id,
            targetId: mergeTarget.id,
          }),
        },
        session,
      );

      if (response.ok) {
        toast.success(
          `Tickets samengevoegd: H${mergeSource.id} → H${mergeTarget.id}`,
        );
        setMergeModalOpen(false);
        setMergeSource(null);
        setMergeTarget(null);
        await refreshTickets();
      } else {
        const error = await response.json();
        toast.error(`Fout bij samenvoegen: ${error.message}`);
      }
    } catch (error) {
      console.error("Error merging tickets:", error);
      toast.error("Fout bij samenvoegen van tickets");
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteModal.ticket) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${deleteModal.ticket.id}`,
        { method: "DELETE" },
        session,
      );

      if (response.ok) {
        toast.success(`Ticket H${deleteModal.ticket.id} verwijderd`);
        setDeleteModal({ open: false, ticket: null });
        await refreshTickets();
      } else {
        throw new Error("Failed to delete ticket");
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
      toast.error("Fout bij verwijderen van ticket");
    }
  };

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Header with View Mode Toggle */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Helpdesk Tickets
                </h1>
              </div>
            </div>

            {/* View Mode Toggle and Add Button */}
            <div className="flex items-center gap-3">
              <div className="border-input bg-background flex items-center gap-1 rounded-lg border p-1 shadow-sm">
                <button
                  onClick={() => navigate("/helpdesk/list")}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                  aria-label="Lijstweergave"
                  title="Lijstweergave"
                >
                  <ListBulletsIcon size={18} weight="regular" />
                  <span className="hidden sm:inline">Lijst</span>
                </button>

                <button
                  onClick={() => navigate("/helpdesk/kanban")}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    "bg-moss-primary text-white shadow-sm",
                  )}
                  aria-label="Kanbanweergave"
                  title="Kanbanweergave"
                >
                  <KanbanIcon size={18} weight="fill" />
                  <span className="hidden sm:inline">Kanban</span>
                </button>
              </div>
              <Button onClick={() => setQuickAddModalOpen(true)} size="sm">
                <span className="h-5 w-5 text-lg font-bold">+</span>
                Ticket toevoegen
              </Button>
            </div>
          </div>
        </header>

        {/* Count Badges for Kanban View */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-4">
            {/* Open Tickets Count */}
            <div className="flex flex-col">
              <span className="text-neutral-primary text-2xl font-bold">
                {openCount ?? "..."}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                Open tickets
              </span>
            </div>

            {/* Separator */}
            <div className="h-10 w-px bg-gray-300"></div>

            {/* Completion Phase Tickets */}
            <div className="flex flex-col">
              <span className="text-moss-primary text-2xl font-bold">
                {completionCount ?? "..."}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                In afronding
              </span>
            </div>

            {/* Separator */}
            <div className="h-10 w-px bg-gray-300"></div>

            {/* Total Non-Closed */}
            <div className="flex flex-col">
              <span className="text-moss-primary text-2xl font-bold">
                {totalCount ?? "..."}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                Totaal (excl. voltooid)
              </span>
            </div>

            {/* Current Display Count - Loaded Tickets */}
            <div className="ml-auto flex flex-col items-end">
              <span className="text-sm text-gray-600">
                Toont{" "}
                <span className="font-semibold">
                  {slicedKanbanTickets.length}
                </span>{" "}
                /{" "}
                <span className="font-semibold">
                  {displayedNonClosedTickets}
                </span>
              </span>
              <span className="text-xs text-gray-500">Geladen tickets</span>
            </div>
          </div>
        </div>

        <div className="px-6 pb-32">
          <HelpdeskKanbanView
            tickets={slicedKanbanTickets}
            onUpdate={refreshTickets}
            onUpdateTicket={handleUpdateTicket}
            onTicketClick={(ticket) => {
              navigate(`/helpdesk/${ticket.id}`, {
                additionalParams: { from: "kanban", viewMode: "kanban" },
              });
            }}
            onQuickAdd={() => setQuickAddModalOpen(true)}
            onColumnHeaderClick={handleColumnHeaderClick}
            onFetchAllInColumn={handleFetchAllInColumn}
            columnPagination={columnPagination}
            hiddenColumns={hiddenKanbanColumns}
            onToggleColumnVisibility={handleToggleColumnVisibility}
            fontSize={kanbanFontSize}
            onIncreaseFontSize={handleIncreaseFontSize}
            onDecreaseFontSize={handleDecreaseFontSize}
          />
        </div>
      </div>

      {/* FilterBar at parent level - applies to all views */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={(value) => {
          if (!isRestoringFromURL) {
            setSearchText(value);
          }
        }}
        searchPlaceholder="Zoek tickets op ID, titel, melder..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={true}
        urlParamPrefix=""
        onFiltersFromURL={handleFiltersFromURL}
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* Modals */}
      <QuickAddTicketModal
        isOpen={quickAddModalOpen}
        onClose={() => setQuickAddModalOpen(false)}
        onTicketAdded={refreshTickets}
      />

      {mergeModalOpen && (
        <MergeTicketsModal
          isOpen={mergeModalOpen}
          onClose={() => {
            setMergeModalOpen(false);
            setMergeSource(null);
            setMergeTarget(null);
          }}
          onConfirm={handleMergeConfirm}
          sourceTicket={mergeSource}
          targetTicket={mergeTarget}
          onSourceChange={setMergeSource}
          onTargetChange={setMergeTarget}
          sourceSearchValue={mergeSearchSource}
          targetSearchValue={mergeSearchTarget}
          onSourceSearchChange={setMergeSearchSource}
          onTargetSearchChange={setMergeSearchTarget}
          allTickets={slicedKanbanTickets}
        />
      )}

      <DeleteTicketModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, ticket: null })}
        onConfirm={handleDeleteConfirm}
        ticket={deleteModal.ticket}
        emailCount={deleteModal.emails || 0}
        noteCount={deleteModal.notes || 0}
      />

      <CreateInsuranceIssueModal
        isOpen={insuranceModalOpen}
        onClose={() => {
          setInsuranceModalOpen(false);
        }}
        ticket={selectedTicketForInsurance}
        onSuccess={async () => {
          setInsuranceModalOpen(false);
          await refreshTickets();
          toast.success("Verzekeringszaak succesvol aangemaakt/gekoppeld");
        }}
      />

      <ExcelExportModal
        isOpen={excelExportModalOpen}
        onClose={() => setExcelExportModalOpen(false)}
        onConfirm={handleExcelExportWithColumns}
        availableColumns={HELPDESK_EXPORT_COLUMNS}
      />

      <UniversalPickerModal
        isOpen={bulkPhasePickerOpen}
        onClose={() => setBulkPhasePickerOpen(false)}
        onConfirm={(value) => {
          handleBulkPhaseConfirm(value as TicketInternalStatus);
        }}
        mode="helpdesk-label"
        title="Selecteer nieuwe fase voor geselecteerde tickets"
        config={{
          defaultValue: null,
          allowRemoval: false,
        }}
      />

      <UniversalPickerModal
        isOpen={columnPhasePickerOpen}
        onClose={() => {
          setColumnPhasePickerOpen(false);
        }}
        onConfirm={(value) => {
          handleColumnPhaseConfirm(value as TicketInternalStatus);
        }}
        mode="helpdesk-label"
        title={`Verplaats alle tickets in kolom "${selectedColumnPhase ? getStatusLabel(selectedColumnPhase) : ""}" naar:`}
        config={{
          defaultValue: null,
          allowRemoval: false,
        }}
      />

      <SupplierSuggestionModal
        isOpen={supplierSuggestionOpen}
        onClose={() => {
          setSupplierSuggestionOpen(false);
        }}
        suppliers={suggestedSuppliers}
        label={suggestionForLabel}
        ticketIds={suggestionForTicketIds}
        onApplySupplier={applySuggestedSupplier}
        isUpdating={isUpdatingSupplier}
      />
    </>
  );
};

export const HelpdeskKanbanPage = memo(HelpdeskKanbanPageComponent);
